import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CHART_PALETTE } from '../../../theme/chartColors';

const LineAreaRenderer = ({ data, config, compact, type = 'line' }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="dc-no-data">No data available</div>;
  }

  const { xKey, yKeys = [], yLabels = [], colors = CHART_PALETTE, showLegend = true, showGrid = true } = config || {};
  
  // Robust detection
  const actualXKey = data[0][xKey] !== undefined ? xKey : Object.keys(data[0]).find(k => typeof data[0][k] === 'string') || 'name';
  const actualYKeys = yKeys.length > 0 && data[0][yKeys[0]] !== undefined ? yKeys : [Object.keys(data[0]).find(k => typeof data[0][k] === 'number') || 'value'];

  const ChartComponent = type === 'area' ? AreaChart : LineChart;
  const SeriesComponent = type === 'area' ? Area : Line;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ChartComponent data={data} margin={compact ? { top: 0, right: 10, left: 40, bottom: 80 } : { top: 10, right: 30, left: 50, bottom: 120 }}>
        {type === 'area' && (
          <defs>
            {yKeys.map((key, index) => (
              <linearGradient key={`grad-${key}`} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0}/>
              </linearGradient>
            ))}
          </defs>
        )}
        {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-primary)" />}
        <XAxis 
          dataKey={actualXKey} 
          interval={0}
          angle={-45}
          textAnchor="end"
          height={compact ? 80 : 130}
          tick={{ fontSize: compact ? 9 : 11, fill: 'var(--text-tertiary)' }} 
          axisLine={false} 
          tickLine={false} 
        />
        <YAxis tick={{ fontSize: compact ? 10 : 12, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
        <Tooltip 
          cursor={false}
          allowEscapeViewBox={{ x: false, y: true }}
          contentStyle={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: '8px', zIndex: 1000 }} 
          itemStyle={{ color: 'var(--text-primary)' }}
        />
        {showLegend && (!compact || yKeys.length > 1) && (
          <Legend 
            verticalAlign="top"
            align="center"
            iconType="circle"
            wrapperStyle={{ 
              fontSize: '10px', 
              paddingBottom: '20px',
              visibility: (compact && yKeys.length <= 1) ? 'hidden' : 'visible' 
            }} 
          />
        )}
        
        {actualYKeys.map((key, index) => (
          <SeriesComponent 
            key={key} 
            type="monotone" 
            dataKey={key} 
            name={yLabels[index] || key} 
            stroke={colors[index % colors.length]} 
            fill={type === 'area' ? `url(#grad-${key})` : colors[index % colors.length]} 
            strokeWidth={3} 
            dot={{ r: compact ? 0 : 4 }} 
            activeDot={{ r: 6 }}
          />
        ))}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

export default LineAreaRenderer;
